import { defineStore } from "pinia"
import { ref } from "vue"
import { requestLogin } from "@/services/api"
import * as StorageUtils from "@/utils/StorageUtils"
import { getUsernameFromToken, extractTaxiTeamFromToken, extractCompanyIdFromToken } from "@/utils/tokenUtils"
import router from "@/router"
import { useConfigStore } from "@/stores/config"
import { useBrandStore } from "@/stores/brand"
import { i18n } from "@/locales"
import { analytics } from "@/analytics"
import { setSentryUser, clearSentryUser, captureException } from "@/sentry"
import { ApiError } from "@/utils/ApiError"

export const useAuthStore = defineStore("auth", () => {
  const isLoggedIn = ref(false)
  const currentUser = ref("")
  const loginErrorMessage = ref<string | null>(null)
  // Set when a session ends without the user asking for it, shown once on the login page
  const sessionNotice = ref<string | null>(null)

  async function setupContext(token: string, username: string): Promise<void> {
    const configStore = useConfigStore()
    try {
      await configStore.fetchConfig()
    } catch (error) {
      console.warn("Failed to fetch config:", error)
      if (!(error instanceof ApiError)) {
        captureException(error, { extra: { phase: "config fetch" } })
      }
    }

    analytics.setupUserContext(token, username, configStore.companyInfo)

    // Signed in to the company's storage now, not when the form first asks for the saved places: that sign-in goes
    // through a function that may be starting cold
    if (configStore.companyId) {
      const { companySession } = await import("@/storage")
      companySession(configStore.companyId).catch(error => console.warn("Firebase sign-in failed:", error))
    }

    const taxiTeam = extractTaxiTeamFromToken(token)
    // A failed config fetch keeps the brand from the last session rather than resetting it
    if (configStore.config) useBrandStore().setFromConfig(configStore.config.taxiSystem, taxiTeam?.name)
    setSentryUser({
      username,
      companyId: configStore.companyId || extractCompanyIdFromToken(token) || undefined,
      companyName: configStore.companyName || undefined,
      taxiteamCode: taxiTeam?.code,
      taxiteamName: taxiTeam?.name
    })
  }

  async function login(username: string, password: string): Promise<boolean> {
    const { t } = i18n.global
    loginErrorMessage.value = null
    sessionNotice.value = null

    try {
      const tokens = await requestLogin(username.trim(), password)

      if (!tokens.accessToken || !tokens.refreshToken) {
        loginErrorMessage.value = t("login.errors.serverError")
        return false
      }

      StorageUtils.saveAuthTokens(tokens.accessToken, tokens.refreshToken)
      isLoggedIn.value = true
      currentUser.value = username.trim().toLowerCase()

      await setupContext(tokens.accessToken, currentUser.value)
      analytics.trackLogin()
      return true
    } catch (error: unknown) {
      console.error("Login error:", error)
      if (!(error instanceof ApiError)) {
        captureException(error, { extra: { phase: "login" } })
      }

      if (error instanceof ApiError && error.status === 401) {
        loginErrorMessage.value = t("login.errors.invalidCredentials")
      } else {
        loginErrorMessage.value = t("login.errors.serverError")
      }
      return false
    }
  }

  async function logout(options: { expired?: boolean } = {}): Promise<void> {
    const { t } = i18n.global
    const wasLoggedIn = isLoggedIn.value

    if (wasLoggedIn) {
      analytics.trackLogout()
    }
    analytics.reset()
    clearSentryUser()

    isLoggedIn.value = false
    currentUser.value = ""
    loginErrorMessage.value = null
    sessionNotice.value = options.expired && wasLoggedIn ? t("login.errors.sessionExpired") : null

    useConfigStore().clearConfig()
    StorageUtils.clearAuth()

    // Stores that hold company data are cleared lazily to avoid import cycles
    const { useBookingsStore } = await import("@/stores/bookings")
    const { useDraftStore } = await import("@/stores/draft")
    const { useSavedAddressesStore } = await import("@/stores/savedAddresses")
    useBookingsStore().clear()
    useDraftStore().reset()
    useSavedAddressesStore().clear()
    const { endCompanySession } = await import("@/storage")
    endCompanySession().catch(error => console.warn("Firebase sign-out failed:", error))

    if (router.currentRoute.value.name !== "Login") {
      router.replace({ name: "Login" })
    }
  }

  async function restoreSession(): Promise<void> {
    const token = StorageUtils.getAuthToken()
    if (!token) return

    const username = getUsernameFromToken(token)
    if (!username) {
      StorageUtils.clearAuth()
      return
    }

    currentUser.value = username
    isLoggedIn.value = true

    await setupContext(token, username)
    // A 401 during config fetch logs the user out inside performRequest
    if (isLoggedIn.value) {
      analytics.trackSessionRestored()
    }
  }

  return {
    isLoggedIn,
    currentUser,
    loginErrorMessage,
    sessionNotice,
    login,
    logout,
    restoreSession
  }
})
